import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Message, prepareMessagesForStorage, prepareMessagesFromStorage } from '@/types/supabaseTypes';
import { SpeechToTextService } from '@/utils/speechToText';

type LeadInfo = {
  name?: string;
  email?: string;
  phone?: string;
  message?: string;
};

type ChatbotContextType = {
  messages: Message[];
  sendMessage: (content: string) => Promise<void>;
  addMessage: (message: Message, replace?: boolean) => void;
  clearMessages: () => void;
  isLoading: boolean;
  leadInfo: LeadInfo;
  isSpeaking: boolean;
  toggleSpeech: () => void;
  stopSpeaking: () => void;
  isListening: boolean;
  startVoiceInput: () => void;
  stopVoiceInput: () => void;
  isVoiceSupported: boolean;
};

const ChatbotContext = createContext<ChatbotContextType | undefined>(undefined);

export const ChatbotProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [leadInfo, setLeadInfo] = useState<LeadInfo>({});
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [speechEnabled, setSpeechEnabled] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);
  const speechServiceRef = useRef<SpeechToTextService | null>(null);
  const isVoiceSupported = false;

  useEffect(() => {
    try {
      const audio = new Audio();
      audio.onended = () => setIsSpeaking(false);
      audio.onerror = (e) => {
        console.error('Audio playback error:', e);
        setIsSpeaking(false);
      };
      setAudioElement(audio);
    } catch (error) {
      console.error('Error initializing audio:', error);
    }
    
    return () => {
      if (audioElement) {
        audioElement.pause();
        audioElement.src = '';
      }
    };
  }, []);

  const initializedRef = React.useRef(false);
  
  useEffect(() => {
    if (!initializedRef.current && messages.length === 0) {
      setMessages([
        {
          id: uuidv4(),
          role: 'assistant',
          content: "Kia ora! I'm Max — the digital assistant for ThinkTomorrow. How can I help you today?",
          timestamp: new Date(),
        }
      ]);
      initializedRef.current = true;
    }
  }, [messages.length]);

  const detectLeadInfo = (message: string, previousMessages: Message[]): LeadInfo => {
    const context = previousMessages.map(m => m.content).join(' ').toLowerCase();
    const currentMsg = message.toLowerCase();
    
    let updatedLeadInfo: LeadInfo = {...leadInfo};
    let infoDetected = false;
    
    if (!updatedLeadInfo.email) {
      const emailPattern = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/;
      const emailMatch = message.match(emailPattern);
      
      if (emailMatch && emailMatch[0]) {
        updatedLeadInfo.email = emailMatch[0];
        infoDetected = true;
      }
    }
    
    if (!updatedLeadInfo.phone) {
      const phonePattern = /(\+\d{1,3})?[\s.-]?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}/;
      const phoneMatch = message.match(phonePattern);
      
      if (phoneMatch && phoneMatch[0]) {
        updatedLeadInfo.phone = phoneMatch[0];
        infoDetected = true;
      }
    }
    
    if (!updatedLeadInfo.name) {
      const namePatterns = [
        /my name(?:'s| is)? ([\w\s]+?)(?:\.|\,|\!|\?|$)/i,
        /(?:^|\s)i(?:'m| am) ([\w\s]+?)(?:\.|\,|\!|\?|$)/i,
        /this is ([\w\s]+?)(?:\.|\,|\!|\?|$)/i
      ];
      
      let nameMatch = null;
      for (const pattern of namePatterns) {
        const match = message.match(pattern);
        if (match && match[1]) {
          nameMatch = match[1].trim();
          break;
        }
      }
      
      if (nameMatch && nameMatch.length > 1 && nameMatch.length < 40) {
        updatedLeadInfo.name = nameMatch;
        infoDetected = true;
      }
    }
    
    if (infoDetected) {
      setLeadInfo(updatedLeadInfo);
      
      localStorage.setItem('chatbotLeadInfo', JSON.stringify(updatedLeadInfo));
      
      if (updatedLeadInfo.email || updatedLeadInfo.phone) {
        try {
          supabase.from('chatbot_leads').insert({
            name: updatedLeadInfo.name || 'Unknown',
            email: updatedLeadInfo.email || null,
            phone: updatedLeadInfo.phone || null,
            message: message.substring(0, 500),
            conversation_id: localStorage.getItem('chatbotConversationId') || uuidv4()
          }).then(({ error }) => {
            if (error) {
              console.error('Error saving lead info:', error);
            } else {
              console.log('Lead info saved successfully');
            }
          });
        } catch (err) {
          console.error('Error sending lead info:', err);
        }
      }
    }
    
    return updatedLeadInfo;
  };

  const addMessage = useCallback((message: Message, replace = false) => {
    setMessages(prevMessages => {
      if (replace && prevMessages.length > 0) {
        const newMessages = [...prevMessages];
        newMessages[newMessages.length - 1] = message;
        return newMessages;
      } else {
        return [...prevMessages, message];
      }
    });
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([
      {
        id: uuidv4(),
        role: 'assistant',
        content: "Kia ora! I'm Max — the digital assistant for ThinkTomorrow. How can I help you today?",
        timestamp: new Date(),
      }
    ]);
    
    localStorage.removeItem('chatbotConversationId');
  }, []);

  const sendMessage = async (content: string) => {
    if (!content.trim()) return;
    
    if (!localStorage.getItem('chatbotConversationId')) {
      localStorage.setItem('chatbotConversationId', uuidv4());
    }
    
    const userMessage: Message = {
      id: uuidv4(),
      role: 'user',
      content,
      timestamp: new Date(),
    };
    
    addMessage(userMessage);
    
    const updatedLeadInfo = detectLeadInfo(content, messages);
    
    setIsLoading(true);
    
    try {
      const conversationHistory = [
        ...messages.map(msg => ({
          role: msg.role,
          content: msg.content
        })),
        { role: 'user', content }
      ];
      
      const { data, error } = await supabase.functions.invoke('max-chatbot', {
        body: {
          messages: conversationHistory,
          leadInfo: updatedLeadInfo
        }
      });
      
      if (error) {
        console.error('Error from Max API:', error);
        
        addMessage({
          id: uuidv4(),
          role: 'assistant',
          content: "I'm sorry, I'm having trouble connecting to my brain right now. Please try again in a moment.",
          timestamp: new Date(),
        });
        
        return;
      }
      
      const response = data.message;
      
      const assistantMessage: Message = {
        id: uuidv4(),
        role: 'assistant',
        content: response,
        timestamp: new Date(),
      };
      
      addMessage(assistantMessage);
      
      localStorage.setItem('chatbotMessages', JSON.stringify(prepareMessagesForStorage([...messages, userMessage, assistantMessage])));
      
    } catch (err) {
      console.error('Error sending message:', err);
      
      addMessage({
        id: uuidv4(),
        role: 'assistant',
        content: "I apologize, but I encountered an error processing your request. Please try again later.",
        timestamp: new Date(),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSpeech = useCallback(() => {
    setSpeechEnabled(prev => !prev);
  }, []);

  const stopSpeaking = useCallback(() => {
    if (audioElement) {
      audioElement.pause();
      setIsSpeaking(false);
    }
  }, [audioElement]);

  const startVoiceInput = useCallback(() => {
    toast.info("Voice input is currently disabled");
  }, []);

  const stopVoiceInput = useCallback(() => {
    // No-op function
  }, []);

  useEffect(() => {
    if (!initializedRef.current) {
      try {
        const savedLeadInfo = localStorage.getItem('chatbotLeadInfo');
        if (savedLeadInfo) {
          try {
            const parsedLeadInfo = JSON.parse(savedLeadInfo);
            setLeadInfo(parsedLeadInfo);
          } catch (e) {
            console.error('Error parsing saved lead info:', e);
          }
        }
        
        const savedMessages = localStorage.getItem('chatbotMessages');
        if (savedMessages) {
          try {
            const parsedMessages = JSON.parse(savedMessages);
            const formattedMessages = prepareMessagesFromStorage(parsedMessages);
            
            if (formattedMessages.length > 0) {
              setMessages(formattedMessages);
              initializedRef.current = true;
            }
          } catch (e) {
            console.error('Error parsing saved messages:', e);
          }
        }
      } catch (e) {
        console.error('Error loading chatbot state from localStorage:', e);
      }
    }
  }, []);

  const contextValue = useMemo(() => ({
    messages,
    sendMessage,
    addMessage,
    clearMessages,
    isLoading,
    leadInfo,
    isSpeaking,
    toggleSpeech,
    stopSpeaking,
    isListening,
    startVoiceInput,
    stopVoiceInput,
    isVoiceSupported
  }), [
    messages, 
    sendMessage, 
    addMessage,
    clearMessages,
    isLoading,
    leadInfo,
    isSpeaking,
    toggleSpeech,
    stopSpeaking,
    isListening,
    startVoiceInput,
    stopVoiceInput,
    isVoiceSupported
  ]);

  return (
    <ChatbotContext.Provider value={contextValue}>
      {children}
    </ChatbotContext.Provider>
  );
};

export const useChatbot = () => {
  const context = useContext(ChatbotContext);
  if (context === undefined) {
    throw new Error('useChatbot must be used within a ChatbotProvider');
  }
  return context;
};
